import { useQuery } from '@tanstack/react-query';
import { useDefaultPaginationValues } from 'hooks/use-default-pagination-values';
import { useNotifications } from 'hooks/use-notifications/use-notifications';
import { useStorage } from 'hooks/use-storage/use-storage';
import { useUrlManager } from 'hooks/use-url-manager/use-url-manager';
import { useUser } from 'hooks/use-user/use-user';
import { AppServiceListResponse, AppServiceResponse, listAppServices, listProjectAppServices } from 'sync/app-service-service';
import { QueryOptions } from 'sync/query';
import { SearchResult } from 'types/store';
import { checkAndMaybeNotifyAboutCancelledMaintenance } from 'utils/maintenance';
import { ReactQueryKeys } from 'utils/react-query';
import { APP_SERVICE_LIST_CACHE_TIME } from './use-app-service-list.constants';
import {
  getEntireAppServiceList,
  includeKeySelectorAppServiceResults,
  includeSearchAppServiceListSuggestions,
} from './use-app-service-list.utils';

type UseAppServiceListProps = {
  organizationId: string;
  projectId?: string;
  refetchInterval?: number | false;
  enabled?: boolean;
  onSuccess?: (data: AppServiceListResponse | null) => void;
} & (
  | {
      page?: QueryOptions['page'];
      perPage?: QueryOptions['perPage'];
      sortBy?: QueryOptions['sortBy'];
      sortDirection?: QueryOptions['sortDirection'];
    }
  | {
      entire: boolean;
    }
);

export const useAppServiceList = ({
  organizationId,
  onSuccess,
  projectId,
  refetchInterval = false,
  enabled = true,
  ...props
}: UseAppServiceListProps) => {
  const { createURL } = useUrlManager();
  const { page, perPage, entire, sortBy, sortDirection } = useDefaultPaginationValues(props);

  const queryEnabled = enabled && !!organizationId;

  /**
   * Keep track of notifications that we have already shown to the user.
   */
  const { read: readMaintenanceCancelledAppServices, save: saveMaintenanceCancelledAppServices } = useStorage<
    Record<string, { lastCancelledAt: string; lastCancelledBy: string; hasBeenDismissed: boolean }>
  >({
    storageKey: 'cb:cancelledAppServiceMaintenanceNotifications',
    fallbackValue: {},
  });
  const { user } = useUser();
  const { createNotification } = useNotifications();

  const query = useQuery(
    ReactQueryKeys.APP_SERVICES_LIST(organizationId, page, perPage, entire, sortBy, sortDirection),
    () => {
      if (entire) {
        return getEntireAppServiceList({ organizationId, startPage: page, perPage, sortBy, sortDirection });
      }

      if (projectId) {
        return listProjectAppServices({ organizationId, projectId }, { page, perPage, sortBy, sortDirection });
      }

      return listAppServices({ organizationId }, { page, perPage, sortBy, sortDirection });
    },
    {
      cacheTime: APP_SERVICE_LIST_CACHE_TIME,
      refetchInterval,
      onSuccess: (data) => {
        if (onSuccess) {
          onSuccess(data);
        }

        checkAndMaybeNotifyAboutCancelledMaintenance({
          userId: user?.id,
          entityName: 'app service',
          entitiesToCheck: data?.data.flatMap((appService) => appService.data) || [],
          previousCancelledInfo: readMaintenanceCancelledAppServices,
          createNotification,
          saveLastCancelledInfo: saveMaintenanceCancelledAppServices,
        });
      },
      enabled: queryEnabled,
    }
  );

  const appServiceList = query.data?.data.filter((record) => record.data) || [];

  function search(term: string, maxResults?: number): SearchResult[] {
    if (!term || appServiceList.length === 0) {
      return [];
    }

    const results: SearchResult[] = [];
    const searchTerm = term.toLowerCase();
    const set = new Set<string>();

    const addUniqueResult = (record: AppServiceResponse, rank: number) => {
      // Quit if we have reached a desired maximum result size.
      if (maxResults && set.size >= maxResults) {
        return;
      }
      // Because we can search multiple properties we can have duplicates.
      // Deduplicate by ID;
      const { id } = record.data;
      if (set.has(id)) {
        return;
      }
      results.push({
        href: createURL('/app-service/app-endpoints', {
          organizationId: record.data.tenantId,
          projectId: record.data.project.id,
          databaseId: record.data.cluster.id,
          appServiceId: record.data.id,
        }),
        icon: 'app-services',
        rank,
        resource: 'appService',
        title: record.data.name,
        summary: `App Service in project ${record.data.project.name}`,
        isFocused: false,
      });

      set.add(id);
    };

    includeSearchAppServiceListSuggestions({
      searchTerm,
      appServiceList,
      addUniqueResult,
    });

    // If our set is populated we've found an exact match on an ID and we can
    // stop looking. There won't be another GUID like this one!
    if (set.size !== 0) {
      return results.sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
    }

    includeKeySelectorAppServiceResults({ addUniqueResult, appServiceList, maxResults, resultSet: set, searchTerm });

    // Return a sorted list of results with the highest rank first.
    // comment my here
    return results.sort((a, b) => {
      return b.rank - a.rank;
    });
  }

  return { query, appServiceList, search };
};
